import React from 'react';
import { ShieldCheck, RefreshCw, TrendingUp, HeartHandshake, ArrowRight, Clock, Sparkles } from 'lucide-react';
import { companyInfo } from '../../data/companyInfo';

interface LongTermCareSectionProps {
  onStartProject: () => void;
  onOpenConsultation: () => void;
}

export const LongTermCareSection: React.FC<LongTermCareSectionProps> = ({ onStartProject, onOpenConsultation }) => {
  const commitment = companyInfo.longTermCommitment;

  const coreGuarantees = [
    {
      icon: HeartHandshake,
      title: "Enduring Client Partnership",
      desc: "We don't launch your website and vanish. We act as your dedicated engineering and marketing team month after month."
    },
    {
      icon: RefreshCw,
      title: "Zero Software Decay",
      desc: "Web technology moves fast. We proactively handle platform updates, security patches, plugin renewals, and modern browser standards."
    },
    {
      icon: TrendingUp,
      title: "Persistent Inbound Business",
      desc: "Continuous SEO monitoring, keyword adaptations, and Google Ads management to ensure your site steadily generates commercial leads."
    },
    {
      icon: Clock,
      title: "Direct SLA & Rapid Response",
      desc: "Emergency bug fixes, rapid turnaround on new feature rollouts, and weekly/monthly performance check-ins."
    }
  ];

  return (
    <section className="bg-gradient-to-b from-[#071426] via-[#050A13] to-[#071426] py-20 border-b border-[#1b2f4a] relative overflow-hidden">
      {/* Background radial glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[350px] bg-[#159DE8]/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-14 space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#0B2342] border border-[#1b2f4a] text-xs font-semibold uppercase tracking-wider text-[#FF851B]">
            <HeartHandshake size={14} className="text-[#159DE8]" />
            <span>OUR VISION & MISSION COMMITMENT</span>
          </div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white font-display tracking-tight leading-tight">
            We Take Care of Your Website Long-Term So You Win Business Regularly
          </h2>

          <p className="text-[#D5DFEC] text-sm sm:text-base leading-relaxed">
            {commitment.description}
          </p>
        </div>

        {/* 4 Core Partnership Guarantees */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {coreGuarantees.map((item, idx) => {
            const Icon = item.icon;
            return (
              <div
                key={idx}
                className="p-6 rounded-2xl bg-[#0c1a2d] border border-[#1b2f4a] hover:border-[#159DE8]/50 transition-all duration-300 shadow-card-dark flex flex-col justify-between group"
              >
                <div className="space-y-3">
                  <div className="w-12 h-12 rounded-xl bg-[#0B2342] border border-[#1b2f4a] flex items-center justify-center text-[#FF851B] group-hover:scale-105 group-hover:border-[#159DE8] transition-all">
                    <Icon size={22} className="text-[#159DE8] group-hover:text-[#FF851B] transition-colors" />
                  </div>
                  <h3 className="text-base font-bold text-white group-hover:text-[#FF851B] transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-xs text-[#8FA0B8] leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Deep Dive Pillars Breakdown Box */}
        <div className="p-8 sm:p-10 rounded-3xl bg-[#050A13] border border-[#159DE8]/30 shadow-2xl space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-[#1b2f4a]">
            <div>
              <span className="text-[11px] font-mono text-[#159DE8] uppercase tracking-wider block">
                Post-Launch SLA Framework
              </span>
              <h3 className="text-lg sm:text-xl font-bold text-white font-display">
                What Our Ongoing Website Care & Growth Retainer Includes:
              </h3>
            </div>
            <div className="text-xs text-[#00C853] bg-green-500/10 px-3 py-1 rounded-full border border-green-500/30 font-semibold self-start sm:self-auto">
              Included in All Partner Engagements
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 text-xs sm:text-sm">
            {commitment.pillars.map((pillar, i) => (
              <div key={i} className="p-4 rounded-xl bg-[#071426] border border-[#1b2f4a] flex items-start gap-3.5">
                <span className="w-6 h-6 rounded-lg bg-[#0B2342] text-[#FF851B] flex items-center justify-center font-mono font-bold text-xs shrink-0 mt-0.5">
                  0{i + 1}
                </span>
                <div className="space-y-1">
                  <h4 className="font-bold text-white text-sm">{pillar.title}</h4>
                  <p className="text-xs text-[#8FA0B8] leading-relaxed">{pillar.detail}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="pt-4 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-[#1b2f4a]">
            <span className="text-xs text-[#8FA0B8]">
              Ready to build a website that delivers business every month?
            </span>

            <div className="flex items-center gap-3 w-full sm:w-auto">
              <button
                onClick={onStartProject}
                className="w-full sm:w-auto px-6 py-2.5 rounded-full bg-gradient-to-r from-[#FF5A36] to-[#FF851B] text-white text-xs font-bold shadow-md hover:shadow-glow-orange transition-all flex items-center justify-center gap-2"
              >
                <span>Start Your Project</span>
                <ArrowRight size={14} />
              </button>

              <button
                onClick={onOpenConsultation}
                className="w-full sm:w-auto px-5 py-2.5 rounded-full bg-[#0B2342] border border-[#1b2f4a] hover:border-white text-white text-xs font-semibold transition-all"
              >
                <span>Discuss Maintenance Plan</span>
              </button>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};
